import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { plainToInstance } from 'class-transformer';
import { MessageBoxHelper } from '../../../components/dialogs/messagebox/messagebox.dialog';
import { TranslateFormatText } from '../../../helpers/array.helpers';
import { CapsuleHelper } from '../../../helpers/capsule.helper';
import { NotificationHelper } from '../../../helpers/notification.helper';
import { PermissionHelper } from '../../../helpers/permissions.helper';
import { TYPE_DICT } from '../../../models/datamodel/adbinfo.model';
import { DataSource } from '../../../models/datamodel/datasource.model';
import { MessageBoxButtons } from '../../../models/enums/messageboxbuttons.enum';
import { MessageBoxIcon } from '../../../models/enums/messageboxicon.enum';
import { MessageBoxResult } from '../../../models/enums/messageboxresult.enum';
import { TranslatedString } from '../../../models/translatedstring.model';
import { DataModelService } from '../../../services/datamodel.service';
import { StandardRequestBase } from '../../../services/request-base';
import { DataCheck } from '../../../workflow/workflow.dialog';
import { BaseListSettings, DeleteTexts, SaveTexts } from '../../base.list.settings';
import { ADataModelDetail } from '../common/adatamodeldetail';

@Component({
    selector: 'datasource-settings',
    templateUrl: '../../base.list.settings.html'
})
export class DataSourceSettings extends BaseListSettings {

    constructor(private translate: TranslateService, private dataService: DataModelService, protected cdRef: ChangeDetectorRef) {
        super(cdRef);
        this.ShowDelete = PermissionHelper.IsAdmin();
    }

    getContentType() {
        return DataSourceDetail;
    }

    loadList(handler) {
        if (this.InitArgs) {
            this.dataService.GetDataSources(this.InitArgs.SID).subscribe(sources => {
                const list = [];
                sources.forEach((source) => {
                    list.push({
                        Caption: source.Caption,
                        ID: source.SID,
                        IsCapsule: source.IsCapsule,
                        IsOverridden: source.IsOverridden
                    });
                });
                handler(list);
            });
        }
    }

    loadData(data) {
        this.dataService.GetDataSource(data).subscribe(datasource => {
            const ds = plainToInstance(DataSource, datasource);
            this.setSelectedItem(ds);
        });
    }

    getNewItem() {
        const ds = new DataSource();
        const name = 'DataSource_';
        let length = 1;
        if (this.ListItems.length > 0) {
            length = this.ListItems.length + 1;
        }
        ds.Name = name + length;
        if (this.InitArgs) {
            ds.DataModelID = this.InitArgs.SID;
        }
        return ds;
    }

    getDeleteText(sel): DeleteTexts {
        const retVal = new DeleteTexts();
        retVal.Question = new TranslateFormatText('@@Sind Sie sicher, dass Sie die Datenquelle \'{0}\' loeschen moechten?');
        retVal.Question.FormatParams.push(sel.Caption);
        retVal.Success = new TranslateFormatText('@@Datenquelle \'{0}\' erfolgreich geloescht.');
        retVal.Success.FormatParams.push(sel.Caption);
        retVal.Title = new TranslateFormatText('@@Datenquelle loeschen');
        return retVal;
    }

    getSaveSuccessText(sel): SaveTexts {
        let caption = TranslatedString.GetTranslation(sel.Caption);
        if (!caption) {
            caption = sel.Name;
        }
        const retVal = new SaveTexts();
        retVal.Text = new TranslateFormatText('@@Datenquelle \'{0}\' erfolgreich gespeichert.');
        retVal.Text.FormatParams.push(caption);
        retVal.Title = new TranslateFormatText('@@Datenquelle speichern');
        return retVal;
    }

    delete(data, handler) {
        this.dataService.DeleteSource(data).subscribe((res) => {
            handler(res);
        });
    }

    async checkHasChanges() {
        if (this.HasChanges) {
            const retVal = await MessageBoxHelper.ShowDialog(new TranslateFormatText('@@Wollen Sie die Aenderungen speichern?'),
                new TranslateFormatText('@@Frage'), MessageBoxButtons.YesNoAbort, MessageBoxIcon.Question);
            if (retVal === MessageBoxResult.Yes) {
                if (this.Component && this.Component.SelectedItem && this.Component.SelectedItem.Connection && this.Component.SelectedItem.Connection.indexOf('Password') > -1) {
                    let conn = JSON.parse(this.Component.SelectedItem.Connection);
                    if (!conn.Password) {
                        let passwd = await MessageBoxHelper.ShowDialog(new TranslateFormatText('@@Es wurde kein Passwort gesetzt, wollen Sie die Aenderungen speichern?'),
                            new TranslateFormatText('@@Frage'), MessageBoxButtons.YesNoAbort, MessageBoxIcon.Question);
                        if (passwd === MessageBoxResult.Yes) {
                            return await this.save(false);
                        } else if (passwd === MessageBoxResult.Abort) {
                            return false;
                        } else { // No
                            this.setHasChanges(false);
                        }
                    } else {
                        return await this.save(false);
                    }
                } else {
                    return await this.save(false);
                }
            } else if (retVal === MessageBoxResult.Abort) {
                return false;
            } else { // No
                this.setHasChanges(false);
            }
        }
        return true;
    }
    protected async checkCanSave(): Promise<DataCheck> {
        const retVal = new DataCheck();
        if (this.Component) {
            const selItem = this.Component.SelectedItem;
            if (selItem) {
                if (selItem.DBInfo) {
                    if (this.Component.SelectedDBInfo && this.Component.SelectedDBInfo.OnlyAgent && !selItem.DBInfo.AgentId) {
                        retVal.Error = '@@Bitte setzen Sie einen Agent';
                        retVal.IsCorrect = false;
                    }
                } else {
                    retVal.Error = '@@Bitte definieren Sie eine Datenquelle';
                    retVal.IsCorrect = false;
                }
            }
        }
        return retVal;
    }

    saveInternal(item, handler) {
        this.dataService.SaveDataSource(item).subscribe(result => {
            if (result) {
                handler(result, result.SID, result.Caption);
            }
        });
    }

    handleNew(item, result) {
        item.SID = result.SID;
        item.Version = result.Version;
        item.InternalID = result.InternalID;
    }

    updateListItem(item, result) {
        item.IsCapsule = result.IsCapsule;
        item.IsOverridden = result.IsOverridden;
    }
}

@Component({
    selector: 'datasource-detail',
    templateUrl: './datasource.settings.html',
    styleUrls: ['./datasource.settings.css']
})
export class DataSourceDetail extends ADataModelDetail {
    Drivers = [];
    Agents = [];
    SelectedDBInfo;
    Subscription;
    HasCapsules = false;

    @ViewChild('dynamic', { read: ViewContainerRef }) viewContainerRef: ViewContainerRef;

    @Input()
    get DataSource() {
        return this.SelectedItem;
    }
    set DataSource(val) {
        if (val != this.SelectedItem) {
            this.setSelectedItem(val);
            this.DataSourceChange.emit(val);
        }
    }
    @Output() DataSourceChange = new EventEmitter<any>();

    get SelectedAgent() {
        if (this.SelectedItem && this.SelectedItem.DBInfo && this.SelectedItem.DBInfo.AgentId) {
            return this.SelectedItem.DBInfo.AgentId;
        }
        return '0';
    }
    set SelectedAgent(val) {
        if (this.SelectedItem && this.SelectedItem.DBInfo) {
            if (val === '0') {
                this.SelectedItem.DBInfo.AgentId = null;
                this.SelectedItem.DBInfo.UseAgent = false;
            } else {
                this.SelectedItem.DBInfo.AgentId = val;
                this.SelectedItem.DBInfo.UseAgent = true;
            }
        }
    }

    constructor(private translate: TranslateService, private dataService: DataModelService, private agentService: StandardRequestBase) {
        super();
    }

    ngOnInit() {
        this.dataService.GetDrivers().subscribe(drivers => {
            if (drivers) {
                this.Drivers = drivers.filter(x => TYPE_DICT.has(x.DBInfoType));
            }
            this.SetData();
        });
        this.agentService.executeGet('config/api/agent', 'GetAllAgents').subscribe(agents => {
            const agentList = [];
            agentList.push({
                Name: this.translate.instant('@@Keine Auswahl'),
                Id: '0'
            });
            if (agents) {
                agentList.push(...agents);
            }
            this.Agents = agentList;
            this.SetData();
        });
        CapsuleHelper.GetCapsuleTags().then(r => {
            this.HasCapsules = r != null;
        });
    }

    setSelectedItem(item) {
        super.setSelectedItem(item);
        this.SetData();
    }
    SetData() {
        let selectedDBInfo;
        if (this.SelectedItem && this.Drivers && this.Agents) {
            if (this.SelectedItem.Connection) {
                this.SelectedItem.DBInfo = JSON.parse(this.SelectedItem.Connection);
                const type = TYPE_DICT.get(this.SelectedItem.DBInfoType);
                if (type) {
                    this.SelectedItem.DBInfo = plainToInstance(type.DBInfoType, this.SelectedItem.DBInfo);
                }
            }
            selectedDBInfo = this.Drivers.find(x => x.DBInfoType === this.SelectedItem.DBInfoType);
        }
        this.SelectedDBInfo = selectedDBInfo;
        this.OnDriverChanged(true);
    }

    OnDriverChanged(internal) {
        if (this.Subscription) {
            this.Subscription.unsubscribe();
        }
        if (this.viewContainerRef) {
            this.viewContainerRef.clear();
            if (this.SelectedDBInfo) {
                const type = TYPE_DICT.get(this.SelectedDBInfo.DBInfoType);
                if (type) {
                    const comp = this.viewContainerRef.createComponent(type.ControlType);
                    const component: any = comp.instance;
                    if (component) {
                        component.DataSource = this.SelectedItem;
                        this.Subscription = component.OnDataChange.subscribe(() => { this.OnItemChanged(); });
                    }
                }
            }
        }
        if (!internal) {
            this.OnItemChanged();
        }
    }
    Initialize(InitArgs) {
        if (InitArgs) {
            this.SelectedItem = InitArgs.Data;
        }
    }
    GetDialogResult() {
        return this.SelectedItem;
    }
    checkConnection() {
        this.dataService.CheckConnection(this.SelectedItem).subscribe(x => {
            if (x.Successfull) {
                NotificationHelper.Success('@@Successfully connected', '@@Success');
            } else {
                NotificationHelper.Error(x.Error, '@@Error');
            }
        });
    }
}
